import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, Chip, List, ListItem, ListItemIcon, ListItemText, Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  Emergency as EmergentIcon,
  CheckCircle as MetIcon,
  Error as GapIcon,
} from '@mui/icons-material';
import type { EmergentAnalysisView } from '../../../../../services/MedNecService';

interface EmergentAnalysisPanelProps {
  analysis: EmergentAnalysisView;
}

const EmergentAnalysisPanel: React.FC<EmergentAnalysisPanelProps> = ({ analysis }) => (
  <Accordion defaultExpanded>
    <AccordionSummary expandIcon={<ExpandIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmergentIcon color="error" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Emergent Admission Analysis</Typography>
        <Chip
          size="small"
          label={analysis.recommendation.replace(/_/g, ' ')}
          color={
            analysis.recommendation === 'EMRG_YES' ? 'success'
              : analysis.recommendation === 'EMRG_POSS' ? 'warning'
              : 'error'
          }
          variant="outlined"
        />
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {analysis.emergentCriteriaMet.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Emergent Criteria Met:</Typography>
            <List dense disablePadding>
              {analysis.emergentCriteriaMet.map((item, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><MetIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {analysis.emergentCriteriaGaps.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Emergent Criteria Gaps:</Typography>
            <List dense disablePadding>
              {analysis.emergentCriteriaGaps.map((item, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><GapIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {analysis.additionalDocumentationNeeded.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Additional Documentation Needed:</Typography>
            <List dense disablePadding>
              {analysis.additionalDocumentationNeeded.map((item, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><GapIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {analysis.samplePhysicianStatement && (
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff3e0' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Sample Physician Statement:
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {analysis.samplePhysicianStatement}
            </Typography>
          </Paper>
        )}
      </Box>
    </AccordionDetails>
  </Accordion>
);

export default EmergentAnalysisPanel;
